// NeoGraph Serialization Utilities
//
// Belongs to `@sourcepilot/framework`: no static dep on
// `@sourcepilot/generated`. Schema-aware lookups (`getRelationshipPropertyName`,
// `getRelationshipClass`, `ModelRegistry`) are sourced from the framework
// resolvers, which `@sourcepilot/generated` installs at module load time.

import { resolveRelProperty } from './reverse-property';
import { getFrameworkResolvers } from './resolvers';

/**
 * Base node data structure from backend
 */
export interface NodeData {
  id: string;
  _label?: string;
  [key: string]: unknown;
}

/**
 * Relationship structure in NeoGraph format
 */
export interface NeoGraphRelationship {
  source: string;
  target: string;
  rel: string;
  properties?: Record<string, any>;
}

/**
 * NeoGraph format from backend (neograph serialization)
 */
export interface NeoGraphFormat<T extends NodeData = NodeData> {
  root_nodes: T[];
  nodes: T[];
  relationships: NeoGraphRelationship[];
  timestamp?: string;
}

/**
 * Graph node with reconstructed relationships
 */
export interface GraphNode<T extends NodeData = NodeData> {
  data: T;
  relationships: Map<string, GraphNode<NodeData> | GraphNode<NodeData>[]>;
}

/**
 * Relationship index entry
 */
interface RelationshipIndexEntry {
  target: string;
  rel: string;
  direction: 'outgoing';
}

/**
 * Parse NeoGraph format and reconstruct object graph
 */
export class NeoGraphParser<T extends NodeData = NodeData> {
  private nodes: Map<string, GraphNode<T>> = new Map();
  private relationshipsByNode: Map<string, RelationshipIndexEntry[]> = new Map();

  /**
   * Parse a NeoGraph response into a graph structure
   */
  parse(neoGraph: NeoGraphFormat<T>): GraphNode<T>[] {
    const { getRelationshipPropertyName, getRelationshipClass, modelRegistry: ModelRegistry } = getFrameworkResolvers();

    // First pass: create graph nodes for all nodes (root_nodes + nodes)
    const allNodes: T[] = [...(neoGraph.root_nodes || []), ...(neoGraph.nodes || [])];

    for (const nodeData of allNodes) {
      const nodeId = nodeData.id;
      if (!nodeId) continue;

      // Skip if already created (root_nodes and nodes might overlap)
      if (this.nodes.has(nodeId)) continue;

      const graphNode: GraphNode<T> = {
        data: nodeData,
        relationships: new Map()
      };

      this.nodes.set(nodeId, graphNode);
    }

    // Build a map of property names to Neo4j relationship types for cardinality lookup
    const propToRelType = new Map<string, string>();
    for (const rel of neoGraph.relationships || []) {
      const propName = getRelationshipPropertyName(rel.rel) || rel.rel;
      propToRelType.set(propName, rel.rel);
    }

    // Build relationship index
    for (const rel of neoGraph.relationships || []) {
      if (!this.relationshipsByNode.has(rel.source)) {
        this.relationshipsByNode.set(rel.source, []);
      }

      // The backend no longer ships `rel_property` — every edge is emitted in
      // canonical origin->dest orientation, so `source` is always the origin and
      // the owning property is its `out` property, resolved type-aware from the
      // source model's `relationships` map (the source of truth for polymorphic
      // rels — e.g. `Project.logical_parts` vs `LogicalPart.children`).
      const sourceLabel = this.nodes.get(rel.source)?.data._label;
      const relationshipKey: string =
        resolveRelProperty(
          sourceLabel ? ModelRegistry[sourceLabel] : undefined,
          rel.rel,
          'out',
          getRelationshipClass(rel.rel) as any,
        ) ?? (getRelationshipPropertyName(rel.rel) || rel.rel);

      this.relationshipsByNode.get(rel.source)!.push({
        target: rel.target,
        rel: relationshipKey,  // Use correct property name based on direction
        direction: 'outgoing'
      });
    }

    // Second pass: reconstruct relationships from the flat relationship array
    for (const [sourceId, rels] of Array.from(this.relationshipsByNode.entries())) {
      const sourceNode = this.nodes.get(sourceId);
      if (!sourceNode) continue;

      // Group relationships by type/name
      const relsByType = new Map<string, string[]>();
      for (const rel of rels) {
        if (!relsByType.has(rel.rel)) {
          relsByType.set(rel.rel, []);
        }
        relsByType.get(rel.rel)!.push(rel.target);
      }

      // Create relationship accessors
      for (const [relType, targetIds] of Array.from(relsByType.entries())) {
        const targetNodes = targetIds
          .map((id: string) => this.nodes.get(id))
          .filter((node): node is GraphNode<T> => node !== undefined);

        if (targetNodes.length === 0) {
          // No nodes to add
          continue;
        }

        // Use relationship registry to determine cardinality
        let isMany = false;

        // Get the Neo4j relationship type for this property name
        const neo4jRelType = propToRelType.get(relType);
        if (neo4jRelType) {
          const relClass = getRelationshipClass(neo4jRelType);
          if (relClass && relClass.cardinality) {
            // Use cardinality from relationship class metadata
            isMany = relClass.cardinality === 'm2m' || relClass.cardinality === 'one2m';
          } else {
            // Fall back to heuristic if no metadata found
            isMany = targetNodes.length > 1 || relType.endsWith('s');
          }
        } else {
          // Fall back to heuristic if relationship type not found
          isMany = targetNodes.length > 1 || relType.endsWith('s');
        }

        if (isMany) {
          // Many relationship
          sourceNode.relationships.set(relType, targetNodes);
        } else {
          // One relationship
          sourceNode.relationships.set(relType, targetNodes[0]);
        }
      }
    }

    // Return graph nodes for root_nodes only
    return (neoGraph.root_nodes || [])
      .map(node => this.nodes.get(node.id))
      .filter((node): node is GraphNode<T> => node !== undefined);
  }

  /**
   * Get a specific relationship from a node
   */
  getRelationship<R extends NodeData = T>(
    node: GraphNode<T>,
    relationshipName: string
  ): GraphNode<R> | GraphNode<R>[] | null {
    const rel = node.relationships.get(relationshipName);
    if (rel === undefined) return null;
    return rel as GraphNode<R> | GraphNode<R>[];
  }

  /**
   * Traverse a relationship path (e.g., "thread.steps.model_requests")
   */
  traverse(node: GraphNode<T>, path: string): GraphNode<T> | GraphNode<T>[] | null {
    const parts = path.split('.');
    let current: GraphNode<T> | GraphNode<T>[] | null = node;

    for (const part of parts) {
      if (!current) return null;

      if (Array.isArray(current)) {
        // If current is array, traverse on all items
        const results: (GraphNode<T> | GraphNode<T>[] | null)[] = current
          .map((n: GraphNode<T>) => this.getRelationship(n, part));

        const filtered = results.filter((r): r is GraphNode<T> | GraphNode<T>[] => r !== null);

        if (filtered.length === 0) return null;

        // Flatten if all results are arrays
        const allArrays = filtered.every((r: GraphNode<T> | GraphNode<T>[]) => Array.isArray(r));
        if (allArrays) {
          current = (filtered as GraphNode<T>[][]).flat();
        } else {
          // Mix of single and array results - flatten all
          current = filtered.flatMap((r: GraphNode<T> | GraphNode<T>[]) => Array.isArray(r) ? r : [r]);
        }
      } else {
        current = this.getRelationship(current, part);
      }
    }

    return current;
  }

  /**
   * Convert graph back to plain objects (for JSON serialization)
   */
  toPlainObject<R extends Record<string, unknown> = Record<string, unknown>>(
    node: GraphNode<T>
  ): R {
    const result = { ...node.data } as Record<string, unknown>;

    for (const [relName, relValue] of Array.from(node.relationships.entries())) {
      if (Array.isArray(relValue)) {
        result[relName] = relValue.map((n: GraphNode<NodeData>) => this.toPlainObject(n as unknown as GraphNode<T>));
      } else if (relValue === null) {
        result[relName] = null;
      } else {
        result[relName] = this.toPlainObject(relValue as unknown as GraphNode<T>);
      }
    }

    return result as R;
  }

  /**
   * Get all parsed nodes (for debugging)
   */
  getAllNodes(): Map<string, GraphNode<T>> {
    return new Map(this.nodes);
  }
}

/**
 * Helper function to quickly parse and flatten results
 */
export function parseNeoGraphResult<T extends NodeData, R extends Record<string, unknown> = Record<string, unknown>>(
  neoGraph: NeoGraphFormat<T>
): R[] {
  const parser = new NeoGraphParser<T>();
  const graphNodes = parser.parse(neoGraph);
  return graphNodes.map(gn => parser.toPlainObject<R>(gn));
}

/**
 * Type-safe relationship accessor
 */
export function getRelationship<T extends NodeData, R extends NodeData = T>(
  node: T & { id: string },
  relationshipName: string,
  parser: NeoGraphParser<T>
): GraphNode<R> | GraphNode<R>[] | null {
  const graphNode = parser.getAllNodes().get(node.id);
  if (!graphNode) return null;

  return parser.getRelationship<R>(graphNode, relationshipName);
}
